import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  MapContainer,
  Marker,
  Polyline,
  Popup,
  TileLayer,
  useMap,
} from "react-leaflet";
import L from "leaflet";
import {
  FaArrowDown,
  FaArrowUp,
  FaExternalLinkAlt,
  FaMapMarkerAlt,
  FaRoute,
  FaSyncAlt,
} from "react-icons/fa";
import "./PianificatorePercorsi.css";

const API_BASE_URL =
  process.env.REACT_APP_API_URL ||
  "https://interventi.synology.me:5050";

const HQ_FIXED_ADDRESS = "Via Buriasco 20/6, Torino";
const HQ_GOOGLE_PLACE_ID = "ChIJIUPh8ti99Q0Rbi9i9HysnO0";
const RETURN_MODE_HQ = "hq";
const RETURN_MODE_START = "start";
const RETURN_MODE_NONE = "none";
const MAX_STOPS = 8;
const DAY_START_STORAGE_KEY =
  "betalumina:pianificatore:day-start:v2";
const DEFAULT_DURATION_STORAGE_KEY =
  "betalumina:pianificatore:default-duration:v2";
const FILTER_DATE_STORAGE_KEY =
  "betalumina:pianificatore:filter-date:v3";
const DEFAULT_DAY_START = "08:00";
const DEFAULT_DURATION_MINUTES = 60;
const V3_COMPARISON_REFRESH_MS = 60000;

const getAuthConfig = () => {
  const token = localStorage.getItem("token");
  return {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    withCredentials: true,
  };
};

const formatMinutes = (minutes) => {
  const value = Number(minutes);
  if (!Number.isFinite(value) || value <= 0) return "—";

  const hours = Math.floor(value / 60);
  const mins = Math.round(value % 60);

  if (!hours) return `${mins} min`;
  if (!mins) return `${hours} h`;
  return `${hours} h ${mins} min`;
};

const dateLabel = (value) => {
  if (!value) return "Data non impostata";
  const [year, month, day] = String(value).split("-").map(Number);
  if (!year || !month || !day) return value;

  return new Date(year, month - 1, day).toLocaleDateString("it-IT");
};

const shortTime = (value) =>
  value ? String(value).slice(0, 5) : null;

const realTripClock = (value) => {
  if (!value) return "—";
  const text = String(value).trim();
  const timePart = text.includes(" ") ? text.split(" ")[1] : text;
  return shortTime(timePart) || "—";
};

const realTripStamp = (trip) => {
  const raw =
    trip?.arrivo_il ||
    trip?.partenza_il ||
    trip?.assegnato_il ||
    "";
  const text = String(raw).trim();
  const date = text.slice(0, 10);
  const time = text.includes(" ") ? realTripClock(text) : "—";
  const id = Number(trip?.id);
  const idLabel = Number.isInteger(id) && id > 0 ? `#${id}` : "#—";
  return `${idLabel} · ${date || "data —"} ${time}`.trim();
};

const realStatusLabel = (status) => {
  if (status === "MATCH_AMBIGUO") return "DA VERIFICARE · più viaggi possibili";
  if (status === "FUORI_GIORNATA") return "VIAGGIO FUORI GIORNATA";
  if (status === "NESSUN_MATCH") return "NON ANCORA ESEGUITA";
  return String(status || "NESSUN_MATCH");
};

const normalizeAddressKey = (value) =>
  String(value || "").trim().replace(/\s+/g, " ").toLowerCase();

const isHqAddress = (value) =>
  normalizeAddressKey(value) === normalizeAddressKey(HQ_FIXED_ADDRESS);

const returnModeLabel = (mode) => {
  if (mode === RETURN_MODE_HQ) return "Rientro in sede";
  if (mode === RETURN_MODE_START) return "Rientro alla partenza";
  return "Termina all’ultima tappa";
};

const hasAppointmentData = (item) =>
  Boolean(item?.oraInizio || item?.oraFine);

const defaultAppointmentEnabled = (item, planningDate) =>
  Boolean(
    hasAppointmentData(item) &&
      planningDate &&
      item?.data_giorno === planningDate
  );

const numberedIcon = (label, start = false) =>
  L.divIcon({
    className: "planner-marker-shell",
    html: `<span class="planner-marker ${
      start ? "planner-marker--start" : ""
    }">${label}</span>`,
    iconSize: [32, 32],
    iconAnchor: [16, 16],
  });

function FitPlannerRoute({ geometry = [] }) {
  const map = useMap();

  useEffect(() => {
    if (!Array.isArray(geometry) || geometry.length < 2) return;

    map.fitBounds(geometry, { padding: [28, 28] });
  }, [geometry, map]);

  return null;
}

function PianificatorePercorsi() {
  const [interventions, setInterventions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [planning, setPlanning] = useState(false);
  const [error, setError] = useState("");
  const [filterDate, setFilterDate] = useState(() => {
    const stored = String(
      localStorage.getItem(FILTER_DATE_STORAGE_KEY) || ""
    ).trim();

    return /^\d{4}-\d{2}-\d{2}$/.test(stored) ? stored : "";
  });
  const [selectedIds, setSelectedIds] = useState([]);
  const [startAddress, setStartAddress] = useState(HQ_FIXED_ADDRESS);
  const [returnMode, setReturnMode] = useState(RETURN_MODE_HQ);
  const [plan, setPlan] = useState(null);
  const [manualOrder, setManualOrder] = useState([]);
  const [manualDirty, setManualDirty] = useState(false);
  const [dayStart, setDayStart] = useState(
    () => localStorage.getItem(DAY_START_STORAGE_KEY) || DEFAULT_DAY_START
  );
  const [defaultDurationMinutes, setDefaultDurationMinutes] = useState(
    () => {
      const stored = Number(
        localStorage.getItem(DEFAULT_DURATION_STORAGE_KEY)
      );
      return Number.isFinite(stored) && stored >= 5 && stored <= 720
        ? stored
        : DEFAULT_DURATION_MINUTES;
    }
  );
  const [highPriorityIds, setHighPriorityIds] = useState([]);
  const [appointmentOverrides, setAppointmentOverrides] = useState({});
  const [v3Saving, setV3Saving] = useState(false);
  const [v3Loading, setV3Loading] = useState(false);
  const [v3Refreshing, setV3Refreshing] = useState(false);
  const [savedPlanV3, setSavedPlanV3] = useState(null);
  const [comparisonV3, setComparisonV3] = useState(null);
  const [comparisonUpdatedAt, setComparisonUpdatedAt] = useState("");
  const [v3Message, setV3Message] = useState("");

  const visibleInterventions = useMemo(() => {
    if (!filterDate) return interventions;

    return interventions.filter(
      (item) => item.data_giorno === filterDate
    );
  }, [filterDate, interventions]);

  const selectedSet = useMemo(
    () => new Set(selectedIds),
    [selectedIds]
  );

  const highPrioritySet = useMemo(
    () => new Set(highPriorityIds),
    [highPriorityIds]
  );

  const selectedInterventions = useMemo(
    () =>
      selectedIds
        .map((id) =>
          interventions.find((item) => Number(item.id) === Number(id))
        )
        .filter(Boolean),
    [interventions, selectedIds]
  );

  const isAppointmentEnabled = (item) => {
    if (!hasAppointmentData(item)) return false;

    if (
      Object.prototype.hasOwnProperty.call(
        appointmentOverrides,
        item.id
      )
    ) {
      return Boolean(appointmentOverrides[item.id]);
    }

    return defaultAppointmentEnabled(item, filterDate);
  };

  const forceAppointmentIds = selectedInterventions
    .filter((item) => appointmentOverrides[item.id] === true)
    .map((item) => item.id);

  const ignoredAppointmentIds = selectedInterventions
    .filter((item) => appointmentOverrides[item.id] === false)
    .map((item) => item.id);

  const hydrateSavedPlanV3 = useCallback((saved) => {
    const snapshot = saved?.snapshot;
    const savedRoute = snapshot?.plan;

    if (!savedRoute || !Array.isArray(savedRoute.stops)) return;

    setPlan(savedRoute);
    setManualOrder(savedRoute.stops);
    setManualDirty(false);
    setSelectedIds(savedRoute.stops.map((stop) => stop.id));
    setStartAddress(
      snapshot.startAddress ||
        savedRoute.start?.address ||
        HQ_FIXED_ADDRESS
    );
    setReturnMode(
      [RETURN_MODE_HQ, RETURN_MODE_START, RETURN_MODE_NONE].includes(
        snapshot.returnMode
      )
        ? snapshot.returnMode
        : savedRoute.returnMode || RETURN_MODE_HQ
    );
    setDayStart(
      shortTime(snapshot.dayStart || savedRoute.planning?.dayStart) ||
        DEFAULT_DAY_START
    );

    const savedDuration = Number(
      snapshot.defaultDurationMinutes ??
        savedRoute.planning?.defaultDurationMinutes
    );
    if (
      Number.isFinite(savedDuration) &&
      savedDuration >= 5 &&
      savedDuration <= 720
    ) {
      setDefaultDurationMinutes(savedDuration);
    }

    setHighPriorityIds(
      Array.isArray(snapshot.highPriorityIds)
        ? snapshot.highPriorityIds
        : []
    );
    setAppointmentOverrides(
      snapshot.appointmentOverrides &&
        typeof snapshot.appointmentOverrides === "object"
        ? snapshot.appointmentOverrides
        : {}
    );
  }, []);

  const loadSavedPlanV3 = useCallback(async (
    planningDate,
    { hydrate = true, silent = false } = {}
  ) => {
    if (!planningDate) {
      setSavedPlanV3(null);
      setComparisonV3(null);
      setV3Message("");
      return null;
    }

    setV3Loading(true);
    if (!silent) setV3Message("");

    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/pianificatore/v3/piano`,
        {
          ...getAuthConfig(),
          params: {
            data: planningDate,
            vehicleKey: "fiorino",
          },
        }
      );

      const saved = response.data?.plan || null;
      const comparison = response.data?.comparison || null;

      setSavedPlanV3(saved);
      setComparisonV3(comparison);
      setComparisonUpdatedAt(
        comparison
          ? new Date().toLocaleTimeString("it-IT")
          : ""
      );

      if (saved && hydrate) {
        hydrateSavedPlanV3(saved);
        setV3Message(
          `Piano salvato caricato · revisione ${saved.revision}`
        );
      } else if (!saved && !silent) {
        setV3Message("Nessun piano V3 salvato per questa giornata.");
      }

      return response.data;
    } catch (err) {
      console.error("Errore caricamento piano V3:", err);
      if (!silent) {
        setV3Message(
          err.response?.data?.message ||
            "Impossibile caricare il piano V3 salvato."
        );
      }
      return null;
    } finally {
      setV3Loading(false);
    }
  }, [hydrateSavedPlanV3]);

  const savePlanV3 = async () => {
    if (!filterDate) {
      setError(
        "Per salvare il piano V3 devi prima selezionare una giornata."
      );
      return;
    }

    if (!plan || manualDirty) {
      setError(
        "Calcola o ricalcola il percorso prima di salvare il piano V3."
      );
      return;
    }

    setV3Saving(true);
    setError("");
    setV3Message("");

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/pianificatore/v3/piano`,
        {
          planningDate: filterDate,
          vehicleKey: "fiorino",
          startAddress,
          returnMode,
          dayStart,
          defaultDurationMinutes: Number(defaultDurationMinutes),
          highPriorityIds,
          appointmentOverrides,
          forceAppointmentIds,
          ignoreAppointmentIds: ignoredAppointmentIds,
          plan: {
            ...plan,
            geometry: [],
            routePoints: [],
          },
        },
        getAuthConfig()
      );

      const saved = response.data?.plan || null;
      const comparison = response.data?.comparison || null;
      setSavedPlanV3(saved);
      setComparisonV3(comparison);
      setComparisonUpdatedAt(
        comparison
          ? new Date().toLocaleTimeString("it-IT")
          : ""
      );
      setV3Message(
        saved
          ? `Piano salvato · revisione ${saved.revision}`
          : "Piano salvato."
      );
    } catch (err) {
      console.error("Errore salvataggio piano V3:", err);
      setError(
        err.response?.data?.message ||
          "Impossibile salvare il piano V3."
      );
    } finally {
      setV3Saving(false);
    }
  };

  const refreshComparisonV3 = useCallback(
    async ({ manual = false } = {}) => {
      if (!filterDate || !savedPlanV3?.id) {
        return null;
      }

      setV3Refreshing(true);
      if (manual) setV3Message("");

      try {
        const response = await axios.get(
          `${API_BASE_URL}/api/pianificatore/v3/piano`,
          {
            ...getAuthConfig(),
            params: {
              data: filterDate,
              vehicleKey: "fiorino",
            },
          }
        );

        const remotePlan = response.data?.plan || null;
        const comparison = response.data?.comparison || null;

        const remoteChanged =
          remotePlan &&
          (Number(remotePlan.id) !== Number(savedPlanV3.id) ||
            Number(remotePlan.revision) !==
              Number(savedPlanV3.revision));

        if (remoteChanged) {
          return await loadSavedPlanV3(filterDate, {
            hydrate: true,
            silent: false,
          });
        }

        setComparisonV3(comparison);
        setComparisonUpdatedAt(
          new Date().toLocaleTimeString("it-IT")
        );

        if (manual) {
          setV3Message("Confronto Flotta aggiornato.");
        }

        return response.data;
      } catch (err) {
        console.error(
          "Errore aggiornamento confronto V3:",
          err
        );

        if (manual) {
          setV3Message(
            err.response?.data?.message ||
              "Impossibile aggiornare il confronto Flotta."
          );
        }

        return null;
      } finally {
        setV3Refreshing(false);
      }
    },
    [
      filterDate,
      loadSavedPlanV3,
      savedPlanV3?.id,
      savedPlanV3?.revision,
    ]
  );

  const comparisonForStop = (index) =>
    Array.isArray(comparisonV3?.stops)
      ? comparisonV3.stops.find(
          (item) => Number(item.ordine) === index + 1
        )
      : null;

  const signedDelta = (value, suffix = "") => {
    if (value === null || value === undefined || value === "") return "—";
    const number = Number(value);
    if (!Number.isFinite(number)) return "—";
    if (number === 0) return `0${suffix}`;
    return `${number > 0 ? "+" : ""}${number}${suffix}`;
  };

  const loadInterventions = async () => {
    setLoading(true);
    setError("");

    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/pianificatore/interventi`,
        getAuthConfig()
      );

      setInterventions(
        Array.isArray(response.data?.interventions)
          ? response.data.interventions
          : []
      );
    } catch (err) {
      console.error("Errore caricamento interventi pianificatore:", err);
      setError(
        err.response?.data?.message ||
          "Impossibile caricare gli interventi Da fare."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadInterventions();
  }, []);

  useEffect(() => {
    if (filterDate) {
      loadSavedPlanV3(filterDate);
    } else {
      setSavedPlanV3(null);
      setComparisonV3(null);
      setComparisonUpdatedAt("");
      setV3Message("");
    }
  }, [filterDate, loadSavedPlanV3]);

  useEffect(() => {
    if (!filterDate || !savedPlanV3?.id) {
      return undefined;
    }

    const refreshIfVisible = () => {
      if (document.visibilityState === "visible") {
        refreshComparisonV3();
      }
    };

    const timerId = window.setInterval(
      refreshIfVisible,
      V3_COMPARISON_REFRESH_MS
    );

    document.addEventListener(
      "visibilitychange",
      refreshIfVisible
    );

    return () => {
      window.clearInterval(timerId);
      document.removeEventListener(
        "visibilitychange",
        refreshIfVisible
      );
    };
  }, [
    filterDate,
    refreshComparisonV3,
    savedPlanV3?.id,
  ]);

  useEffect(() => {
    if (filterDate) {
      localStorage.setItem(FILTER_DATE_STORAGE_KEY, filterDate);
    } else {
      localStorage.removeItem(FILTER_DATE_STORAGE_KEY);
    }
  }, [filterDate]);

  useEffect(() => {
    localStorage.setItem(DAY_START_STORAGE_KEY, dayStart);
  }, [dayStart]);

  useEffect(() => {
    localStorage.setItem(
      DEFAULT_DURATION_STORAGE_KEY,
      String(defaultDurationMinutes)
    );
  }, [defaultDurationMinutes]);

  const resetComputedPlan = () => {
    setPlan(null);
    setManualOrder([]);
    setManualDirty(false);
  };

  const toggleIntervention = (item) => {
    if (!item?.indirizzo) return;

    setSelectedIds((current) => {
      if (current.includes(item.id)) {
        setHighPriorityIds((ids) =>
          ids.filter((id) => id !== item.id)
        );
        setAppointmentOverrides((current) => {
          const next = { ...current };
          delete next[item.id];
          return next;
        });
        resetComputedPlan();
        return current.filter((id) => id !== item.id);
      }

      if (current.length >= MAX_STOPS) {
        setError(`La V2 supporta al massimo ${MAX_STOPS} tappe.`);
        return current;
      }

      setError("");
      resetComputedPlan();
      return [...current, item.id];
    });
  };

  const toggleHighPriority = (id) => {
    setHighPriorityIds((current) =>
      current.includes(id)
        ? current.filter((value) => value !== id)
        : [...current, id]
    );
    resetComputedPlan();
  };

  const toggleAppointment = (id) => {
    const item = interventions.find(
      (entry) => Number(entry.id) === Number(id)
    );

    if (!item || !hasAppointmentData(item)) return;

    const currentEnabled = isAppointmentEnabled(item);

    setAppointmentOverrides((current) => ({
      ...current,
      [id]: !currentEnabled,
    }));

    resetComputedPlan();
  };

  const requestPlan = async ({
    ids = selectedIds,
    preserveOrder = false,
  } = {}) => {
    const normalizedAddress = startAddress.trim();

    if (!normalizedAddress) {
      setError("Inserisci il punto di partenza.");
      return;
    }

    if (!ids.length) {
      setError("Seleziona almeno un intervento da pianificare.");
      return;
    }

    setPlanning(true);
    setError("");

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/pianificatore/ottimizza`,
        {
          startAddress: normalizedAddress,
          interventionIds: ids,
          returnMode,
          preserveOrder,
          dayStart,
          defaultDurationMinutes: Number(defaultDurationMinutes),
          highPriorityIds,
          planningDate: filterDate || null,
          forceAppointmentIds,
          ignoreAppointmentIds: ignoredAppointmentIds,
        },
        getAuthConfig()
      );

      const nextPlan = response.data;
      setPlan(nextPlan);
      setManualOrder(
        Array.isArray(nextPlan?.stops) ? nextPlan.stops : []
      );
      setManualDirty(false);

      const optimizedIds = Array.isArray(nextPlan?.stops)
        ? nextPlan.stops.map((stop) => stop.id)
        : ids;

      setSelectedIds(optimizedIds);
      setSavedPlanV3(null);
      setComparisonV3(null);
      if (filterDate) {
        setV3Message(
          "Piano calcolato: salva la giornata per attivare il confronto Flotta."
        );
      }
    } catch (err) {
      console.error("Errore pianificazione percorso:", err);
      setError(
        err.response?.data?.message ||
          "Non è stato possibile calcolare il percorso."
      );
    } finally {
      setPlanning(false);
    }
  };

  const moveStop = (index, delta) => {
    setManualOrder((current) => {
      const target = index + delta;

      if (target < 0 || target >= current.length) return current;

      const next = [...current];
      [next[index], next[target]] = [next[target], next[index]];
      setManualDirty(true);
      return next;
    });
  };

  const recalculateManual = () => {
    requestPlan({
      ids: manualOrder.map((stop) => stop.id),
      preserveOrder: true,
    });
  };

  const openGoogleMaps = () => {
    if (
      manualDirty ||
      !Array.isArray(plan?.stops) ||
      plan.stops.length < 1
    ) {
      return;
    }

    const orderedStops = plan.stops.filter(
      (stop) => String(stop?.indirizzo || "").trim()
    );

    if (orderedStops.length !== plan.stops.length) {
      setError(
        "Impossibile aprire Google Maps: una tappa non ha un indirizzo valido."
      );
      return;
    }

    const originValue = String(plan.start?.address || startAddress).trim();
    const finalAddress = String(plan.end?.address || "").trim();

    const destinationValue =
      plan.returnMode === RETURN_MODE_NONE
        ? orderedStops[orderedStops.length - 1].indirizzo
        : finalAddress;

    if (!originValue || !destinationValue) {
      setError(
        "Impossibile aprire Google Maps: partenza o destinazione finale non valida."
      );
      return;
    }

    const waypointStops =
      plan.returnMode === RETURN_MODE_NONE
        ? orderedStops.slice(0, -1)
        : orderedStops;

    const params = new URLSearchParams({
      api: "1",
      origin: originValue,
      destination: destinationValue,
      travelmode: "driving",
    });

    if (isHqAddress(originValue)) {
      params.set("origin_place_id", HQ_GOOGLE_PLACE_ID);
    }

    if (isHqAddress(destinationValue)) {
      params.set("destination_place_id", HQ_GOOGLE_PLACE_ID);
    }

    if (waypointStops.length) {
      params.set(
        "waypoints",
        waypointStops.map((stop) => stop.indirizzo).join("|")
      );
    }

    window.open(
      `https://www.google.com/maps/dir/?${params.toString()}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  const geometry = Array.isArray(plan?.geometry)
    ? plan.geometry
    : [];

  return (
    <section
      className="route-planner-page"
      data-build="BETALUMINA_ROUTE_PLANNER_V3"
    >
      <div className="route-planner-hero">
        <div>
          <span className="route-planner-kicker">
            <FaRoute /> Pianificazione giornaliera
          </span>
          <h3>Pianificatore percorsi</h3>
          <p>
            Organizza la giornata considerando viabilità, durate,
            appuntamenti e priorità operative.
          </p>
        </div>

        <button
          type="button"
          className="planner-refresh"
          onClick={loadInterventions}
          disabled={loading || planning}
        >
          <FaSyncAlt /> Aggiorna
        </button>
      </div>

      <div className="planner-config-grid">
        <div className="planner-config-card">
          <label htmlFor="planner-start-address">Punto di partenza</label>
          <div className="planner-input-icon">
            <FaMapMarkerAlt />
            <input
              id="planner-start-address"
              type="text"
              value={startAddress}
              onChange={(event) => {
                setStartAddress(event.target.value);
                resetComputedPlan();
              }}
              placeholder="Indirizzo di partenza"
              autoComplete="street-address"
            />
          </div>
          <button
            type="button"
            className="planner-use-hq"
            disabled={isHqAddress(startAddress)}
            onClick={() => {
              setStartAddress(HQ_FIXED_ADDRESS);
              resetComputedPlan();
            }}
          >
            Usa sede BetaLumina
          </button>
          <small>
            Precompilato con Via Buriasco 20/6. Puoi partire da
            casa, da un fornitore o da un altro indirizzo.
          </small>
        </div>

        <div className="planner-config-card">
          <label htmlFor="planner-day">Filtra per giornata</label>
          <input
            id="planner-day"
            type="date"
            value={filterDate}
            onChange={(event) => {
              setFilterDate(event.target.value);
              setSelectedIds([]);
              setHighPriorityIds([]);
              setAppointmentOverrides({});
              resetComputedPlan();
            }}
          />
          <small>
            Se scegli una giornata, gli orari degli interventi di
            quella data diventano vincolanti di default. Lascia vuoto
            per pianificare liberamente.
          </small>
        </div>

        <div className="planner-config-card">
          <label htmlFor="planner-start-time">Orario di partenza</label>
          <input
            id="planner-start-time"
            type="time"
            value={dayStart}
            onChange={(event) => {
              setDayStart(event.target.value || DEFAULT_DAY_START);
              resetComputedPlan();
            }}
          />
          <small>
            Orario da cui il motore costruisce ETA, attese e fine giornata.
          </small>
        </div>

        <div className="planner-config-card">
          <label htmlFor="planner-default-duration">
            Durata default
          </label>
          <input
            id="planner-default-duration"
            type="number"
            min="5"
            max="720"
            step="5"
            value={defaultDurationMinutes}
            onChange={(event) => {
              const value = Number(event.target.value);
              if (Number.isFinite(value)) {
                setDefaultDurationMinutes(value);
                resetComputedPlan();
              }
            }}
          />
          <small>
            Minuti usati solo se l'intervento non ha già una durata.
          </small>
        </div>

        <div className="planner-config-card planner-return-card">
          <label htmlFor="planner-return-mode">Fine del giro</label>
          <select
            id="planner-return-mode"
            value={returnMode}
            onChange={(event) => {
              setReturnMode(event.target.value);
              resetComputedPlan();
            }}
          >
            <option value={RETURN_MODE_HQ}>
              Rientro alla sede BetaLumina
            </option>
            <option value={RETURN_MODE_START}>
              Rientro al punto di partenza
            </option>
            <option value={RETURN_MODE_NONE}>
              Termina all'ultimo intervento
            </option>
          </select>
          <small>
            La sede resta fissa; il punto di partenza può essere diverso.
          </small>
        </div>
      </div>

      {error && <div className="planner-error">{error}</div>}

      <div className="planner-workspace">
        <div className="planner-selection-card">
          <div className="planner-section-head">
            <div>
              <strong>Interventi disponibili</strong>
              <span>
                {visibleInterventions.length} visibili ·{" "}
                {selectedIds.length}/{MAX_STOPS} selezionati
              </span>
            </div>
          </div>

          {loading ? (
            <div className="planner-empty">Caricamento interventi...</div>
          ) : visibleInterventions.length === 0 ? (
            <div className="planner-empty">
              Nessun intervento Da fare
              {filterDate
                ? " nella giornata selezionata."
                : " disponibile."}
            </div>
          ) : (
            <div className="planner-interventions">
              {visibleInterventions.map((item) => {
                const selected = selectedSet.has(item.id);
                const routable = Boolean(
                  String(item.indirizzo || "").trim()
                );

                return (
                  <label
                    key={item.id}
                    className={`planner-intervention ${
                      selected ? "selected" : ""
                    } ${!routable ? "disabled" : ""}`}
                  >
                    <input
                      type="checkbox"
                      checked={selected}
                      disabled={!routable}
                      onChange={() => toggleIntervention(item)}
                    />

                    <div>
                      <div className="planner-intervention-title">
                        <strong>
                          {item.numero_foglio_intervento || `#${item.id}`}
                        </strong>
                        <span>
                          {item.cliente || "Cliente non indicato"}
                        </span>
                      </div>

                      <p>{item.causale || "Causale non indicata"}</p>

                      <small>
                        {routable
                          ? item.indirizzo
                          : "Indirizzo mancante — non pianificabile"}
                      </small>

                      <div className="planner-intervention-meta">
                        <span>{dateLabel(item.data_giorno)}</span>
                        {hasAppointmentData(item) && (
                          <span>
                            Orario{" "}
                            {shortTime(item.oraInizio) || "—"}
                            {"–"}
                            {shortTime(item.oraFine) || "—"}
                          </span>
                        )}
                        {Number(item.durataMinuti) > 0 && (
                          <span>
                            Durata {formatMinutes(item.durataMinuti)}
                          </span>
                        )}
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>
          )}

          {selectedInterventions.length > 0 && (
            <div className="planner-v2-options">
              <div className="planner-v2-options-head">
                <strong>Vincoli del piano</strong>
                <span>
                  Gli orari diventano vincolanti automaticamente
                  solo se la giornata selezionata coincide con la data
                  dell’intervento. Puoi comunque forzarli manualmente.
                </span>
              </div>

              {selectedInterventions.map((item) => {
                const hasAppointment = hasAppointmentData(item);
                const appointmentEnabled =
                  isAppointmentEnabled(item);
                const appointmentOverridden =
                  Object.prototype.hasOwnProperty.call(
                    appointmentOverrides,
                    item.id
                  );
                const appointmentMatchesDay =
                  defaultAppointmentEnabled(item, filterDate);

                return (
                  <div className="planner-v2-option-row" key={item.id}>
                    <div>
                      <strong>
                        {item.numero_foglio_intervento || `#${item.id}`}
                      </strong>
                      <small>{item.cliente || "—"}</small>
                    </div>

                    <button
                      type="button"
                      className={`planner-v2-chip ${
                        highPrioritySet.has(item.id) ? "active" : ""
                      }`}
                      onClick={() => toggleHighPriority(item.id)}
                    >
                      {highPrioritySet.has(item.id)
                        ? "Priorità alta"
                        : "Priorità normale"}
                    </button>

                    {hasAppointment ? (
                      <button
                        type="button"
                        className={`planner-v2-chip ${
                          appointmentEnabled ? "active" : ""
                        }`}
                        onClick={() => toggleAppointment(item.id)}
                      >
                        {appointmentEnabled
                          ? appointmentOverridden
                            ? "Orario vincolante · manuale"
                            : "Orario vincolante · giornata"
                          : appointmentOverridden
                          ? "Orario ignorato · manuale"
                          : appointmentMatchesDay
                          ? "Orario vincolante · giornata"
                          : filterDate
                          ? "Orario ignorato · data diversa"
                          : "Orario ignorato · nessuna giornata"}
                      </button>
                    ) : (
                      <span className="planner-v2-chip muted">
                        Nessun orario
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          <button
            type="button"
            className="planner-optimize-button"
            disabled={
              planning ||
              selectedIds.length === 0 ||
              !startAddress.trim()
            }
            onClick={() => requestPlan()}
          >
            <FaRoute />
            {planning ? "Calcolo percorso..." : "Ottimizza percorso"}
          </button>
        </div>

        <div className="planner-result-card">
          {!plan ? (
            <div className="planner-result-placeholder">
              <FaRoute />
              <strong>Il percorso ottimizzato comparirà qui</strong>
              <span>
                La V2.2 costruisce una giornata fattibile con ETA,
                durate, appuntamenti coerenti con la data e priorità.
              </span>
            </div>
          ) : (
            <>
              <div className="planner-summary">
                <div>
                  <span>Km previsti</span>
                  <strong>
                    {Number(plan.totalDistanceKm || 0).toFixed(1)}
                  </strong>
                </div>
                <div>
                  <span>Tempo guida</span>
                  <strong>
                    {formatMinutes(plan.totalDurationMinutes)}
                  </strong>
                </div>
                <div>
                  <span>Tempo lavoro</span>
                  <strong>
                    {formatMinutes(plan.planning?.serviceMinutes)}
                  </strong>
                </div>
                <div>
                  <span>Attese</span>
                  <strong>
                    {Number(plan.planning?.waitingMinutes || 0) === 0
                      ? "0 min"
                      : formatMinutes(plan.planning?.waitingMinutes)}
                  </strong>
                </div>
                <div>
                  <span>Fine prevista</span>
                  <strong>{plan.planning?.dayEnd || "—"}</strong>
                </div>
                <div>
                  <span>Tappe</span>
                  <strong>{plan.stops?.length || 0}</strong>
                </div>
                <div>
                  <span>Fine giro</span>
                  <strong>{returnModeLabel(plan.returnMode)}</strong>
                </div>
              </div>

              <div
                className="planner-v3-panel"
                data-planned-real-ui="BETALUMINA_PLANNED_VS_REAL_UI_V3_1"
                data-auto-refresh="BETALUMINA_PLANNED_VS_REAL_AUTOREFRESH_V3_2"
              >
                <div className="planner-v3-head">
                  <div>
                    <strong>V3.2 · Piano giornata / Flotta</strong>
                    <span>
                      Il piano previsto resta congelato; il confronto con i
                      viaggi reali Teltonika/Traccar si aggiorna da solo.
                    </span>
                  </div>

                  <div className="planner-v3-actions">
                    <button
                      type="button"
                      className="planner-v3-refresh"
                      onClick={() =>
                        refreshComparisonV3({ manual: true })
                      }
                      disabled={
                        v3Refreshing ||
                        v3Loading ||
                        !filterDate ||
                        !savedPlanV3
                      }
                    >
                      <FaSyncAlt
                        className={
                          v3Refreshing
                            ? "planner-v3-refresh-icon is-spinning"
                            : "planner-v3-refresh-icon"
                        }
                      />
                      {v3Refreshing
                        ? "Aggiornamento..."
                        : "Aggiorna confronto"}
                    </button>

                    <button
                      type="button"
                      className="planner-v3-save"
                      onClick={savePlanV3}
                      disabled={
                        v3Saving ||
                        v3Loading ||
                        !filterDate ||
                        !plan ||
                        manualDirty
                      }
                    >
                      {v3Saving
                        ? "Salvataggio..."
                        : savedPlanV3
                        ? "Aggiorna piano giornata"
                        : "Salva piano giornata"}
                    </button>
                  </div>
                </div>

                <div className="planner-v3-status">
                  <span>
                    {filterDate
                      ? `Giornata ${dateLabel(filterDate)}`
                      : "Seleziona una giornata per usare V3"}
                  </span>
                  {savedPlanV3 && (
                    <span className="saved">
                      Salvato · rev. {savedPlanV3.revision}
                    </span>
                  )}
                  {savedPlanV3 && (
                    <span className="live">
                      Reale auto · 60 s
                      {comparisonUpdatedAt
                        ? ` · ${comparisonUpdatedAt}`
                        : ""}
                    </span>
                  )}
                  {v3Loading && <span>Caricamento piano salvato...</span>}
                  {v3Message && <span>{v3Message}</span>}
                </div>

                {comparisonV3?.summary && (
                  <div className="planner-v3-summary">
                    <div>
                      <span>Tappe abbinate</span>
                      <strong>
                        {comparisonV3.summary.matchedStops}/
                        {comparisonV3.summary.totalStops}
                      </strong>
                    </div>
                    <div>
                      <span>Km previsti abbinati</span>
                      <strong>
                        {Number(
                          comparisonV3.summary
                            .plannedMatchedDistanceKm || 0
                        ).toFixed(1)}
                      </strong>
                    </div>
                    <div>
                      <span>Km reali abbinati</span>
                      <strong>
                        {Number(
                          comparisonV3.summary
                            .realMatchedDistanceKm || 0
                        ).toFixed(1)}
                      </strong>
                    </div>
                    <div>
                      <span>Guida prevista</span>
                      <strong>
                        {formatMinutes(
                          comparisonV3.summary
                            .plannedMatchedDriveMinutes
                        )}
                      </strong>
                    </div>
                    <div>
                      <span>Guida reale</span>
                      <strong>
                        {formatMinutes(
                          comparisonV3.summary
                            .realMatchedDriveMinutes
                        )}
                      </strong>
                    </div>
                    <div>
                      <span>Non eseguite</span>
                      <strong>
                        {comparisonV3.summary.notMatchedStops || 0}
                      </strong>
                    </div>
                    <div>
                      <span>Da verificare</span>
                      <strong>
                        {comparisonV3.summary.ambiguousStops || 0}
                      </strong>
                    </div>
                    <div>
                      <span>Fuori giornata</span>
                      <strong>
                        {comparisonV3.summary.outsideDayStops || 0}
                      </strong>
                    </div>
                    <div>
                      <span>Viaggi extra</span>
                      <strong>
                        {comparisonV3.summary.extraTrips || 0}
                      </strong>
                    </div>
                    <div>
                      <span>Viaggi annullati</span>
                      <strong>
                        {comparisonV3.summary.cancelledTrips || 0}
                      </strong>
                    </div>
                  </div>
                )}

                {Array.isArray(comparisonV3?.extraTrips) &&
                  comparisonV3.extraTrips.length > 0 && (
                    <div className="planner-v3-real status-fuori_giornata">
                      <strong>VIAGGI EXTRA · non presenti nel piano</strong>
                      {comparisonV3.extraTrips.map((trip) => (
                        <span key={`extra-${trip.id}`}>
                          {realTripStamp(trip)} · intervento{" "}
                          {trip.intervento_id || "—"}
                        </span>
                      ))}
                    </div>
                  )}

                {Array.isArray(comparisonV3?.cancelledTrips) &&
                  comparisonV3.cancelledTrips.length > 0 && (
                    <div className="planner-v3-real status-nessun_match">
                      <strong>VIAGGI ANNULLATI · esclusi dal confronto</strong>
                      {comparisonV3.cancelledTrips.map((trip) => (
                        <span key={`cancelled-${trip.id}`}>
                          {realTripStamp(trip)} · intervento{" "}
                          {trip.intervento_id || "—"}
                        </span>
                      ))}
                    </div>
                  )}
              </div>

              {geometry.length > 1 && (
                <div className="planner-map-wrap">
                  <MapContainer
                    center={geometry[0]}
                    zoom={11}
                    scrollWheelZoom
                    className="planner-map"
                  >
                    <TileLayer
                      attribution="&copy; OpenStreetMap contributors"
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    <FitPlannerRoute geometry={geometry} />
                    <Polyline
                      positions={geometry}
                      pathOptions={{ weight: 5, opacity: 0.78 }}
                    />

                    <Marker
                      position={[
                        plan.start.latitude,
                        plan.start.longitude,
                      ]}
                      icon={numberedIcon("S", true)}
                    >
                      <Popup>
                        <strong>{plan.start.label || "Punto di partenza"}</strong>
                        <br />
                        {plan.start.address}
                      </Popup>
                    </Marker>

                    {plan.end &&
                      normalizeAddressKey(plan.end.address) !==
                        normalizeAddressKey(plan.start.address) && (
                        <Marker
                          position={[
                            plan.end.latitude,
                            plan.end.longitude,
                          ]}
                          icon={numberedIcon("F")}
                        >
                          <Popup>
                            <strong>
                              {plan.end.label || "Destinazione finale"}
                            </strong>
                            <br />
                            {plan.end.address}
                          </Popup>
                        </Marker>
                      )}

                    {(plan.stops || []).map((stop, index) => (
                      <Marker
                        key={stop.id}
                        position={[
                          stop.latitude,
                          stop.longitude,
                        ]}
                        icon={numberedIcon(String(index + 1))}
                      >
                        <Popup>
                          <strong>
                            {index + 1}.{" "}
                            {stop.cliente ||
                              stop.numero_foglio_intervento}
                          </strong>
                          <br />
                          {stop.indirizzo}
                        </Popup>
                      </Marker>
                    ))}
                  </MapContainer>
                </div>
              )}

              <div className="planner-order-head">
                <div>
                  <strong>Ordine delle tappe</strong>
                  <span>
                    {manualDirty
                      ? "Ordine modificato: ricalcola prima di navigare."
                      : plan.optimization?.method === "manual"
                      ? "Ordine manuale ricalcolato."
                      : "Ordine ottimizzato automaticamente."}
                  </span>
                </div>

                <button
                  type="button"
                  className="planner-google-button"
                  onClick={openGoogleMaps}
                  disabled={manualDirty}
                >
                  <FaExternalLinkAlt /> Google Maps
                </button>
              </div>

              <div className="planner-stop-list">
                {manualOrder.map((stop, index) => {
                  const leg =
                    !manualDirty && plan.legs?.[index]
                      ? plan.legs[index]
                      : null;

                  return (
                    <div className="planner-stop" key={stop.id}>
                      <div className="planner-stop-index">
                        {index + 1}
                      </div>

                      <div className="planner-stop-main">
                        <strong>
                          {stop.numero_foglio_intervento ||
                            stop.cliente ||
                            `Intervento ${stop.id}`}
                        </strong>
                        <span>{stop.cliente || "—"}</span>
                        <small>{stop.indirizzo}</small>

                        {leg && (
                          <em>
                            Dalla tappa precedente:{" "}
                            {leg.distanceKm.toFixed(1)} km ·{" "}
                            {formatMinutes(leg.durationMinutes)}
                          </em>
                        )}

                        {!manualDirty && stop.planning && (
                          <div className="planner-stop-schedule">
                            <span>
                              Arrivo {stop.planning.arrivalTime || "—"}
                            </span>
                            <span>
                              Inizio {stop.planning.serviceStartTime || "—"}
                            </span>
                            <span>
                              Fine {stop.planning.serviceEndTime || "—"}
                            </span>
                            <span>
                              Durata{" "}
                              {formatMinutes(stop.planning.durationMinutes)}
                            </span>
                            {stop.planning.waitingMinutes > 0 && (
                              <span>
                                Attesa{" "}
                                {formatMinutes(stop.planning.waitingMinutes)}
                              </span>
                            )}
                            {stop.planning.useAppointment && (
                              <span className="appointment">
                                Appuntamento
                                {stop.planning.appointmentStart
                                  ? ` ${stop.planning.appointmentStart}`
                                  : ""}
                                {stop.planning.appointmentEnd
                                  ? `–${stop.planning.appointmentEnd}`
                                  : ""}
                              </span>
                            )}
                            {stop.planning.priority === "high" && (
                              <span className="priority">
                                Priorità alta
                              </span>
                            )}
                          </div>
                        )}

                        {!manualDirty &&
                          (() => {
                            const real = comparisonForStop(index);
                            if (!real) return null;

                            if (real.matchStatus === "MATCH_UNICO") {
                              return (
                                <div className="planner-v3-real">
                                  <strong>
                                    REALE · viaggio #
                                    {real.matchedTrip?.id || "—"} agganciato
                                  </strong>
                                  <span>
                                    Partenza{" "}
                                    {realTripClock(
                                      real.matchedTrip?.partenza_il
                                    )}
                                  </span>
                                  <span>
                                    Arrivo{" "}
                                    {realTripClock(
                                      real.matchedTrip?.arrivo_il
                                    )}
                                  </span>
                                  <span>
                                    {real.deltas?.realKm != null
                                      ? `${Number(
                                          real.deltas.realKm
                                        ).toFixed(1)} km`
                                      : "Km —"}
                                  </span>
                                  <span>
                                    {real.deltas?.realDriveMinutes != null
                                      ? `${Math.round(
                                          Number(
                                            real.deltas.realDriveMinutes
                                          )
                                        )} min guida`
                                      : "Guida —"}
                                  </span>
                                  <span className="delta">
                                    ETA{" "}
                                    {signedDelta(
                                      real.deltas?.arrivoDeltaMin,
                                      " min"
                                    )}
                                  </span>
                                  <span className="delta">
                                    Km{" "}
                                    {signedDelta(
                                      real.deltas?.kmDelta,
                                      " km"
                                    )}
                                  </span>
                                  <span className="delta">
                                    Guida{" "}
                                    {signedDelta(
                                      real.deltas?.guidaDeltaMin,
                                      " min"
                                    )}
                                  </span>
                                </div>
                              );
                            }

                            return (
                              <div
                                className={`planner-v3-real status-${String(
                                  real.matchStatus || "NESSUN_MATCH"
                                ).toLowerCase()}`}
                              >
                                <strong>
                                  {realStatusLabel(real.matchStatus)}
                                </strong>
                                <span>{real.reason}</span>
                                {Array.isArray(real.candidates) &&
                                  real.candidates.length > 0 && (
                                    <span>
                                      Candidati:{" "}
                                      {real.candidates
                                        .map((trip) => realTripStamp(trip))
                                        .join(" · ")}
                                    </span>
                                  )}
                              </div>
                            );
                          })()}
                      </div>

                      <div className="planner-stop-actions">
                        <button
                          type="button"
                          title="Sposta prima"
                          disabled={index === 0}
                          onClick={() => moveStop(index, -1)}
                        >
                          <FaArrowUp />
                        </button>
                        <button
                          type="button"
                          title="Sposta dopo"
                          disabled={index === manualOrder.length - 1}
                          onClick={() => moveStop(index, 1)}
                        >
                          <FaArrowDown />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>

              {manualDirty && (
                <button
                  type="button"
                  className="planner-recalculate-button"
                  disabled={planning}
                  onClick={recalculateManual}
                >
                  <FaSyncAlt />
                  {planning
                    ? "Ricalcolo..."
                    : "Ricalcola ordine manuale"}
                </button>
              )}

              {!manualDirty &&
                plan.end &&
                plan.legs?.length > manualOrder.length && (
                  <div className="planner-return-leg">
                    {plan.returnMode === RETURN_MODE_HQ
                      ? "Rientro in sede"
                      : "Rientro al punto di partenza"}
                    :{" "}
                    {plan.legs[
                      plan.legs.length - 1
                    ].distanceKm.toFixed(1)}{" "}
                    km ·{" "}
                    {formatMinutes(
                      plan.legs[plan.legs.length - 1].durationMinutes
                    )}
                  </div>
                )}
            </>
          )}
        </div>
      </div>

      <div className="planner-footnote">
        <strong>V3.2</strong>
        <span>
          Il motore di pianificazione V2.2 resta invariato. V3 aggiunge
          il salvataggio persistente della giornata e il confronto
          previsto/reale usando i viaggi Flotta Teltonika/Traccar già
          registrati. Un abbinamento dubbio viene segnalato come ambiguo:
          non viene mai scelto arbitrariamente. OBD e rerouting dinamico
          non sono necessari per questa versione.
        </span>
      </div>
    </section>
  );
}

export default PianificatorePercorsi;
